import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, ImagePlus, Check, AlertTriangle, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { compressImageVariants } from "@/lib/compressImage";
import { convertHeicToJpeg } from "@/lib/heic";
import { uploadVideoChunked } from "@/lib/chunkedVideoUpload";
import { computeFingerprint } from "@/lib/fingerprint";
import { trackEvent } from "@/lib/track";

// How many photos are compressed/uploaded at once.
const CONCURRENCY = 10;

function describeError(err) {
  const detail = err?.response?.data?.detail;
  if (detail) return formatApiErrorDetail(detail);
  if (err?.message) return err.message;
  return "errore sconosciuto";
}

// Runs `worker` over `items` with at most `limit` in flight at once, preserving
// each item's own result/error via the returned settled array.
async function runWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let nextIndex = 0;
  async function runNext() {
    while (nextIndex < items.length) {
      const i = nextIndex++;
      try {
        results[i] = { ok: true, value: await worker(items[i], i) };
      } catch (err) {
        results[i] = { ok: false, error: err };
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runNext));
  return results;
}

// Shared by Gallery's own button and the Hero CTA -- a guest uploads a photo or
// video but never gets to browse what anyone (including themselves) uploaded;
// the organizer moderates everything in the dashboard and decides separately
// whether/when to publish a curated album.
export default function UploadModal({ slug, open, onClose }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // { ok: [name,...], duplicate: [name,...], failed: [{name, reason}, ...] }
  const [name, setName] = useState(() => localStorage.getItem("gallery_name") || "");
  const [caption, setCaption] = useState("");
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null); // { completed, total, pct }
  const photoRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (open) trackEvent(slug, "upload_opened");
  }, [open, slug]);

  if (!open) return null;

  const close = () => {
    if (uploading) return;
    onClose();
    setResult(null);
    setFiles([]);
    setCaption("");
    setProgress(null);
  };

  // Photos and videos use separate <input>s (see below) rather than one mixed
  // picker: on iOS, asking the system Photos picker to hand over a mixed batch
  // of large videos and photos in one go makes the "copy into a temp sandbox
  // file" step it does internally more likely to silently fail (it's flaky,
  // not a hard size limit -- the same batch can work on one attempt and not
  // the next). Splitting the two keeps each handoff smaller. Picks accumulate
  // across both buttons instead of replacing each other.
  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0 && e.target.files && e.target.files.length === 0) {
      toast.error("Nessun file selezionato. Riprova con meno file alla volta (per i video, massimo 3-4 insieme).");
      return;
    }
    // A 0-byte file here means iOS handed the input a placeholder instead of
    // the real bytes -- typically a video only stored in iCloud ("Ottimizza
    // spazio") that it failed to download. Catching it now, before compression
    // or upload even starts, turns a confusing later failure into a clear
    // message right when the user picks the file.
    const usable = picked.filter((f) => f.size > 0);
    if (usable.length < picked.length) {
      toast.error(`${picked.length - usable.length} file scartati (vuoti): apri il video nell'app Foto per qualche secondo per scaricarlo da iCloud, poi riprova.`);
    }
    setFiles((prev) => [...prev, ...usable]);
    e.target.value = "";
  };

  // Returns true if the file was a duplicate already in the gallery (skipped,
  // not re-sent); false if freshly uploaded. `onBytes` reports real transfer
  // progress for videos; for photos (fast, small after compression) it's just
  // called once on completion -- fine-grained mid-flight progress matters far
  // less for a 1-2MB upload than for a multi-hundred-MB video.
  const uploadOne = async (file, onBytes) => {
    const isVideo = file.type.startsWith("video/");
    if (isVideo) {
      const { duplicate } = await uploadVideoChunked(slug, file, {
        caption: caption.trim(),
        guestName: name.trim(),
        onProgress: (loaded) => onBytes(loaded),
      });
      onBytes(file.size);
      return duplicate;
    }

    const fingerprint = await computeFingerprint(file);
    const converted = await convertHeicToJpeg(file);
    // One decode shared by both variants (not two separate ones) -- halves
    // peak memory per photo, which is what let dozens of large photos in a
    // row exhaust the browser's memory and stall the whole batch.
    const [full, thumb] = await compressImageVariants(converted, [{ maxDim: 2500, quality: 0.85 }, { maxDim: 480, quality: 0.72 }]);
    const fd = new FormData();
    fd.append("file", full);
    fd.append("thumb", thumb);
    fd.append("caption", caption.trim());
    fd.append("guest_name", name.trim());
    fd.append("fingerprint", fingerprint);
    const { data } = await api.post(`/public/${slug}/photos`, fd);
    onBytes(file.size);
    return !!data.duplicate;
  };

  const doUpload = async () => {
    if (!files.length) return toast.error("Seleziona almeno un file");
    setUploading(true);
    setResult(null);
    localStorage.setItem("gallery_name", name.trim());

    const totalBytes = files.reduce((s, f) => s + f.size, 0) || 1;
    const bytesDone = new Array(files.length).fill(0);
    let completed = 0;

    const reportProgress = () => {
      const uploaded = bytesDone.reduce((s, b) => s + b, 0);
      setProgress({ completed, total: files.length, pct: Math.min(100, Math.round((uploaded / totalBytes) * 100)) });
    };
    reportProgress();

    // Videos run in their own pool, separate from photos: a batch of large
    // offloaded-to-iCloud videos downloading+decoding together is exactly the
    // kind of memory pressure that makes iOS Safari silently drop/kill work,
    // per the iCloud "Optimize Storage" failure mode -- set to 3 per explicit
    // request; drop it back down if video batches start stalling again.
    const photoIdx = [], videoIdx = [];
    files.forEach((f, i) => (f.type.startsWith("video/") ? videoIdx : photoIdx).push(i));
    const VIDEO_CONCURRENCY = 3;

    const runPool = (indices, limit) =>
      runWithConcurrency(indices, limit, async (i) => {
        const file = files[i];
        try {
          return await uploadOne(file, (loaded) => { bytesDone[i] = loaded; reportProgress(); });
        } finally {
          // Counted as "done" whether it succeeded or failed -- otherwise a
          // failed file left the progress bar permanently under-reporting how
          // much of the batch had actually been processed.
          bytesDone[i] = file.size;
          completed++;
          reportProgress();
        }
      });

    // runPool returns results aligned to `indices`, not to `files` -- map each
    // back to its original file index before merging the two pools together.
    const settled = new Array(files.length);
    const [photoResults, videoResults] = await Promise.all([
      runPool(photoIdx, CONCURRENCY),
      runPool(videoIdx, VIDEO_CONCURRENCY),
    ]);
    photoIdx.forEach((fileIdx, j) => (settled[fileIdx] = photoResults[j]));
    videoIdx.forEach((fileIdx, j) => (settled[fileIdx] = videoResults[j]));

    const ok = [];
    const duplicate = [];
    const failed = [];
    settled.forEach((s, i) => {
      if (s.ok) (s.value ? duplicate : ok).push(files[i].name);
      else failed.push({ file: files[i], name: files[i].name, reason: describeError(s.error) });
    });

    setUploading(false);
    setProgress(null);
    setFiles([]);
    setResult({ ok, duplicate, failed });
    if (failed.length) toast.error(`${failed.length} su ${files.length} non caricati`);
  };

  const retryFailed = () => {
    setFiles(result.failed.map((f) => f.file));
    setResult(null);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4" onClick={close} data-testid="gallery-upload-modal">
      <div className="w-full max-w-md bg-[#faf9f6] rounded-3xl p-7 space-y-5 text-left" onClick={(e) => e.stopPropagation()}>
        {result ? (
          <div className="text-center py-4" data-testid="gallery-upload-success">
            {result.failed.length === 0 && result.duplicate.length === 0 ? (
              <>
                <Check size={36} className="mx-auto mb-4" style={{ color: "var(--wed-primary)" }} />
                <h3 className="font-wed text-2xl mb-2">Grazie!</h3>
                <p className="text-sm text-neutral-500 mb-6">I tuoi file sono stati inviati agli sposi.</p>
              </>
            ) : (
              <>
                <AlertTriangle size={36} className="mx-auto mb-4 text-amber-500" />
                <h3 className="font-wed text-2xl mb-2">Fatto</h3>
                <p className="text-sm text-neutral-500 mb-1">
                  {result.ok.length} caricati
                  {result.duplicate.length > 0 ? `, ${result.duplicate.length} già presenti (saltati)` : ""}
                  {result.failed.length > 0 ? `, ${result.failed.length} non riusciti` : ""}.
                </p>
                {result.failed.length > 0 && (
                  <div className="text-left bg-white border rounded-xl p-3 my-4 max-h-36 overflow-y-auto" style={{ borderColor: "#e3ddd2" }} data-testid="gallery-upload-failed-list">
                    {result.failed.map((f, i) => (
                      <p key={i} className="text-xs text-neutral-500 truncate">{f.name} — <span className="text-rose-500">{f.reason}</span></p>
                    ))}
                  </div>
                )}
                {result.failed.length > 0 && (
                  <button className="btn-outline-wed w-full flex items-center justify-center gap-2 mb-2" onClick={retryFailed} data-testid="gallery-upload-retry-failed">
                    <RotateCcw size={16} /> Riprova solo i falliti
                  </button>
                )}
              </>
            )}
            <button className="btn-wed w-full" onClick={close} data-testid="gallery-upload-done-close">Chiudi</button>
          </div>
        ) : (
          <>
            <h3 className="font-wed text-2xl">Condividi un ricordo</h3>

            <div>
              <label className="label-wed">Il tuo nome</label>
              <input className="input-wed" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Anna Rossi" disabled={uploading} data-testid="gallery-name-input" />
            </div>

            <div>
              <label className="label-wed">Didascalia (facoltativa)</label>
              <input className="input-wed" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Un ricordo speciale…" disabled={uploading} data-testid="gallery-caption-input" />
            </div>

            {/* Plain MIME wildcards only, no mixed-in extensions (".heic"): on
                Android, combining accept="image/*,video/*" with explicit file
                extensions confuses the system Photo Picker and videos become
                untappable in the "Raccolta fotografica" view. HEIC/HEIF files
                already report as image/* there, so nothing is lost. */}
            <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickFiles} data-testid="gallery-file-input-photo" />
            <input ref={videoRef} type="file" accept="video/*" multiple className="hidden" onChange={onPickFiles} data-testid="gallery-file-input-video" />
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => photoRef.current?.click()}
                disabled={uploading}
                className="border border-dashed rounded-2xl py-6 flex flex-col items-center gap-2 text-neutral-500 hover:text-neutral-700 transition-colors duration-200 disabled:opacity-60"
                style={{ borderColor: "var(--wed-primary)" }}
                data-testid="gallery-file-select-photo"
              >
                <ImagePlus size={24} style={{ color: "var(--wed-primary)" }} />
                <span className="text-sm">Foto</span>
              </button>
              <button
                type="button"
                onClick={() => videoRef.current?.click()}
                disabled={uploading}
                className="border border-dashed rounded-2xl py-6 flex flex-col items-center gap-2 text-neutral-500 hover:text-neutral-700 transition-colors duration-200 disabled:opacity-60"
                style={{ borderColor: "var(--wed-primary)" }}
                data-testid="gallery-file-select-video"
              >
                <Camera size={24} style={{ color: "var(--wed-primary)" }} />
                <span className="text-sm">Video</span>
              </button>
            </div>
            {files.length > 0 && (
              <div className="flex items-center justify-between text-sm" data-testid="gallery-files-summary">
                <span>{files.length} file selezionati</span>
                <button type="button" onClick={() => setFiles([])} disabled={uploading} className="text-neutral-400 underline disabled:opacity-60" data-testid="gallery-files-clear">
                  Svuota
                </button>
              </div>
            )}
            {progress && (
              <div data-testid="gallery-upload-progress">
                <div className="h-2 rounded-full bg-neutral-200 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-200" style={{ width: `${progress.pct}%`, background: "var(--wed-primary)" }} />
                </div>
                <p className="text-xs text-neutral-500 mt-1.5">
                  {progress.completed} di {progress.total} — {progress.pct}%
                </p>
              </div>
            )}

            <button className="btn-wed w-full flex items-center justify-center gap-2" onClick={doUpload} disabled={uploading || !files.length} data-testid="gallery-upload-confirm">
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
              {uploading ? "Caricamento…" : "Carica"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
